import React, { useState, useEffect } from 'react';
import {
  Fab,
  Tooltip,
  Snackbar,
  Alert,
  Button,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import RedeemCouponModal from './RedeemCouponModal';
import { authService } from '../services/authService';

export default function RedeemFloatingButton({ onNavigate }) {
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
  const isLoggedIn = !!token;

  const [isPromotionalOptIn, setIsPromotionalOptIn] = useState(() => {
    const stored = localStorage.getItem('isPromotionalOptIn');
    return stored !== null ? stored === 'true' : null;
  });

  useEffect(() => {
    if (isLoggedIn && userId) {
      authService.getUserProfile(userId)
        .then((res) => {
          if (res && res.data && res.data.isPromotionalOptIn !== undefined) {
            const optIn = Boolean(res.data.isPromotionalOptIn);
            setIsPromotionalOptIn(optIn);
            localStorage.setItem('isPromotionalOptIn', String(optIn));
          }
        })
        .catch((err) => {
          console.error('Failed to fetch user profile for promotional opt-in:', err);
        });
    }
  }, [isLoggedIn, userId]);

  const handleFabClick = () => {
    const currentToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!currentToken) {
      setSnackbarOpen(true);
    } else {
      setRedeemModalOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // After login: if isPromotionalOptIn is true then hide the floating button; if false, show it
  if (isLoggedIn && isPromotionalOptIn === true) {
    return null;
  }

  return (
    <>
      <Tooltip title="Redeem promotional voucher code" arrow placement="left">
        <Fab
          variant="extended"
          id="floating-redeem-button"
          aria-label="Redeem Coupon Code"
          onClick={handleFabClick}
          sx={{
            position: 'fixed',
            top: { xs: '74px', sm: '82px', md: 'auto' },
            bottom: { xs: 'auto', md: 32 },
            right: { xs: 16, sm: 20, md: 32 },
            zIndex: 1050,
            background: 'linear-gradient(135deg, #D4A24C 0%, #B9852E 100%)',
            color: '#0F2E2B',
            fontWeight: 700,
            fontSize: { xs: '12.5px', sm: '13.5px', md: '14.5px' },
            fontFamily: '"Fraunces", serif',
            letterSpacing: '0.02em',
            textTransform: 'none',
            px: { xs: 1.8, sm: 2.2, md: 2.8 },
            py: { xs: 0.6, sm: 0.8, md: 1.2 },
            minHeight: { xs: 38, sm: 44, md: 52 },
            borderRadius: '999px',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            boxShadow: '0 8px 24px rgba(212, 162, 76, 0.4), 0 2px 6px rgba(0, 0, 0, 0.35)',
            animation: 'pulseGlow 2.5s infinite ease-in-out',
            '@keyframes pulseGlow': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(212, 162, 76, 0.55), 0 8px 24px rgba(0, 0, 0, 0.35)',
              },
              '70%': {
                boxShadow: '0 0 0 10px rgba(212, 162, 76, 0), 0 8px 24px rgba(0, 0, 0, 0.35)',
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(212, 162, 76, 0), 0 8px 24px rgba(0, 0, 0, 0.35)',
              },
            },
            transition: 'transform 0.25s ease, background 0.25s ease, box-shadow 0.25s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #E8C078 0%, #D4A24C 100%)',
              transform: { xs: 'scale(1.02)', md: 'translateY(-3px) scale(1.03)' },
              boxShadow: '0 12px 30px rgba(212, 162, 76, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.96)',
            },
          }}
        >
          <ConfirmationNumberIcon sx={{ mr: 0.8, fontSize: { xs: 18, sm: 20, md: 22 }, color: '#0F2E2B' }} />
          Redeem Code
        </Fab>
      </Tooltip>

      {/* Snackbar notification when user is not logged in */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: { xs: 24, sm: 36 },
          zIndex: 1100,
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="warning"
          icon={<ConfirmationNumberIcon sx={{ color: '#D4A24C', fontSize: 22 }} />}
          action={
            onNavigate && (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setSnackbarOpen(false);
                  onNavigate('login');
                }}
                sx={{
                  color: '#0F2E2B',
                  backgroundColor: '#D4A24C',
                  fontWeight: 700,
                  fontSize: '12px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 1.6,
                  py: 0.4,
                  ml: 1.5,
                  '&:hover': {
                    backgroundColor: '#E8C078',
                  },
                }}
              >
                Login
              </Button>
            )
          }
          sx={{
            backgroundColor: '#153A35',
            color: '#F5F1E6',
            border: '1px solid rgba(212, 162, 76, 0.35)',
            borderRadius: '16px',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.55)',
            alignItems: 'center',
            fontSize: '13.5px',
            fontFamily: '"Inter", sans-serif',
            '& .MuiAlert-message': {
              fontWeight: 500,
            },
            '& .MuiAlert-icon': {
              mr: 1.2,
            },
          }}
        >
          Please login to redeem the code
        </Alert>
      </Snackbar>

      {/* Promotional Coupon Redeem Modal */}
      <RedeemCouponModal
        open={redeemModalOpen}
        onClose={() => setRedeemModalOpen(false)}
        onNavigate={onNavigate}
      />
    </>
  );
}
